import { makeStyles } from '@material-ui/styles';
import { useSetAtom } from 'jotai';

import {
    ArrowLeftSLine,
    ArrowRightSLine,
    Text,
    MainButton,
    TextButton,
} from '@bizzabo/design-system';

import { useMeetings, View } from '../../MeetingsTool/MeetingsToolContext';
import { showMeetingsAtom } from '../../../messages';
import MeetNowIcon from './MeetNowIcon';
import MeetingSchedulingIcon from './MeetingSchedulingIcon';
import useTypeTracking from '../../../../analytics';
import { useUserSettings } from '../../../../settingsNav';
import useRealTimeMeetingsEnabled from '../../../hooks/useRealTimeMeetingsEnabled';

interface ZeroStateProps {
    isBackButtonShow?: boolean;
}

const ZeroState: React.FC<ZeroStateProps> = ({ isBackButtonShow }) => {
    const { setView, eventUniqueName } = useMeetings();
    const { data: isRealTimeMeetingsEnabled } = useRealTimeMeetingsEnabled(eventUniqueName);
    const setShowMeetings = useSetAtom(showMeetingsAtom);
    const { openScheduledMeetings } = useUserSettings();
    const { Track, trackClickEvent } = useTypeTracking({
        data: {
            description: {
                location: 'MEETING_WIZARD',
                flow: 'CREATE_MEETING',
                meeting_participant_role: 'HOST',
            },
        },
    });
    const classes = useStyles();

    const realTimeMeetingHandle = () => {
        setShowMeetings(true);
        trackClickEvent({
            subCategory: 'REAL_TIME_MEETINGS',
            description: {
                meeting_type: 'MEET_NOW',
            },
        });
    };

    const scheduleMeetingHandle = () => {
        setView(View.ContactListPage);
        trackClickEvent({
            subCategory: 'SCHEDULED_MEETINGS',
            description: {
                meeting_type: 'SCHEDULED',
            },
        });
    };

    const backHandle = () => {
        setView(View.Manage);
        trackClickEvent({
            subCategory: 'BACK_MAIN_MEETING_AREA',
        });
    };

    const openSettings = () => {
        openScheduledMeetings();
        trackClickEvent({
            subCategory: 'OPEN_MEETING_SETTINGS',
            description: {
                flow: 'EDIT_SETTINGS',
            },
        });
    };

    return (
        <Track>
            <div className={classes.header}>
                {isBackButtonShow && (
                    <div className={classes.topHeader}>
                        <MainButton
                            variant="fill"
                            className={classes.backBtn}
                            onClick={backHandle}
                            before={<ArrowLeftSLine className={classes.iconButton} />}
                        >
                            <Text variant="t80" className={classes.backBtnText}>
                                My meetings
                            </Text>
                        </MainButton>
                    </div>
                )}
                <Text variant="t40">Start networking</Text>
                <div className={classes.row}>
                    <Text variant="t81" className={classes.settingsText}>
                        Start by setting your personal
                    </Text>
                    <TextButton size="small" className={classes.settingsBtn} onClick={openSettings}>
                        Schedule Preference
                    </TextButton>
                </div>
            </div>
            <div className={classes.content}>
                {isRealTimeMeetingsEnabled && (
                    <div className={classes.item} onClick={realTimeMeetingHandle}>
                        <div className={classes.itemHeader}>
                            <Text variant="t40">Start a Spontaneous Meeting</Text>
                            <ArrowRightSLine width="30px" height="30px" />
                        </div>

                        <div className={classes.details}>
                            <div>
                                <MeetNowIcon />
                            </div>
                            <Text variant="t71">
                                {`There's no time like the present! Find, message, and meet other
                                attendees instantly!`}
                            </Text>
                        </div>
                    </div>
                )}
                <div className={classes.item} onClick={scheduleMeetingHandle}>
                    <div className={classes.itemHeader}>
                        <Text variant="t40">Schedule a Meeting</Text>
                        <ArrowRightSLine width="30px" height="30px" />
                    </div>

                    <div className={classes.details}>
                        <div>
                            <MeetingSchedulingIcon />
                        </div>
                        <Text variant="t71">
                            {`Don't worry about the back and forth. Use our smart scheduling tool to
                            select an open time in the future with an attended you'd like to meet.`}
                        </Text>
                    </div>
                </div>
            </div>
        </Track>
    );
};

const useStyles = makeStyles(
    theme => ({
        header: {
            display: 'flex',
            flexDirection: 'column',
            padding: '40px 30px 32px',
            color: theme.customization.background.G30,
        },
        topHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 18,
        },
        backBtn: {
            padding: 0,
            background: 'none',
            color: theme.customization.background.G20,
            '&:hover': {
                background: 'none',
            },
        },
        backBtnText: {
            margin: '1px 0 0',
            cursor: 'pointer',
        },
        iconButton: {
            height: 24,
        },
        row: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 10,
            color: '#767676',
        },
        settingsText: {
            marginRight: 5,
        },
        settingsBtn: {
            ...theme.typography.t81,
            padding: 0,
            marginTop: 1,
            color: '#272727',
            lineHeight: 1,
        },
        content: {
            padding: '0 30px',
        },
        item: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            padding: '20px 16px',
            cursor: 'pointer',
            [theme.breakpoints.up('sm')]: {
                minHeight: 200,
            },
            '&:first-child': {
                padding: '0 16px 20px',
                borderBottom: 'solid 1px #DBDBDB',
                [theme.breakpoints.up('sm')]: {
                    minHeight: 200,
                    padding: '20px 16px',
                },
            },
        },
        itemHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
            [theme.breakpoints.up('sm')]: {
                marginBottom: 20,
            },
        },
        details: {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            [theme.breakpoints.up('sm')]: {
                gap: 20,
            },
        },
    }),
    { classNamePrefix: 'meetings-zero-state' }
);

export default ZeroState;
